<template>
  <div>
    <div class="message-content">
      <span v-if="message.isTyping"
            class="thinking-animation"></span>
      <template v-else v-for="(part, index) in processMessageParts(message)"
                :key="`message-${message.id}-part-${index}`">
        <span v-if="part.type === 'citation'">
          <span> (</span>
          <a class="citation-link"
             href="#"
             @click.prevent="goToPage(part.pageNumber)">
            <span>{{ $t('knowledgeBase.library.reference') }}</span>
          </a>
          <span>)</span>
        </span>
        <span v-else-if="part.type === 'multiPageCitation'">
          <span> ({{ $t('knowledgeBase.library.reference') }}</span>
          <template v-for="(page, pageIndex) in part.pageNumbers" :key="page">
            <a class="citation-link" href="#" @click.prevent="goToPage(page)">
              <span>&nbsp;{{ pageIndex + 1 }}</span>
            </a>
            <span v-if="pageIndex < part.pageNumbers.length - 1">, </span>
          </template>
          <span>)</span>
        </span>
        <span v-else-if="part.type === 'text'" v-html="part.text"></span>
      </template>
    </div>
    <div v-if="message.requestId && !feedbackSubmitted"
         class="feedback-buttons mt-1">
      <button class="button is-borderless"
              @click="giveFeedback('up')">
        <span class="icon has-text-primary"><i class="fas fa-thumbs-up"></i></span>
      </button>
      <button class="button is-borderless ml-1"
              @click="giveFeedback('down')">
        <span class="icon has-text-primary"><i class="fas fa-thumbs-down"></i></span>
      </button>
    </div>
    <div v-if="feedbackSubmitted" class="mt-3">
      <div class="message-content">
        <p>{{ $t('knowledgeBase.feedback.submitted') }}</p>
      </div>
    </div>
    <div v-if="selectingReason"
         class="message-content mt-3 feedback-reason-container">
      <div class="feedback-content">
        <div class="feedback-prompt">
          <p class="feedback-prompt-text">
            {{ $t('knowledgeBase.feedback.prompt') }}
          </p>
          <button class="delete is-small" @click="dismissAdditionalFeedback"></button>
        </div>
        <div class="feedback-reasons">
          <button v-for="reason in feedbackReasons"
                  class="button is-small"
                  :class="{ 'is-primary': selectedReason === reason.key }"
                  :key="reason.key"
                  @click="selectReason(reason.key)">
            {{ reason.label }}
          </button>
        </div>
        <div class="feedback-actions mt-1">
          <button class="button is-small"
                  @click="dismissAdditionalFeedback">
            {{ $t('cancel') }}
          </button>
          <button class="button is-primary is-small"
                  :disabled="!selectedReason"
                  @click="submitNegativeFeedback">
            {{ $t('submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { marked } from 'marked'
import { NegativeFeedbackReasons } from '@/interfaces/library/knowledgeBase'

export default {
  name: 'AiResponseMessage',
  emits: ['content-updated'],
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      feedbackSubmitted: false,
      selectingReason: false,
      selectedReason: null
    }
  },
  /*
   * Feedback and AI response messages changes the content
   * of the sidecar, so we need to emit a content updated
   * event to trigger a visual update.
   */
  watch: {
    feedbackSubmitted() {
      this.$nextTick(() => this.$emit('content-updated'))
    },
    selectingReason() {
      this.$nextTick(() => this.$emit('content-updated'))
    },
    'message.isTyping'(isTyping) {
      if (!isTyping) {
        this.$nextTick(() => this.$emit('content-updated'))
      }
    }
  },
  computed: {
    feedbackReasons() {
      return [
        { key: 'hardToUnderstand', label: this.$t('knowledgeBase.feedback.reasons.hardToUnderstand'), value: NegativeFeedbackReasons.hardToUnderstand },
        { key: 'incorrect', label: this.$t('knowledgeBase.feedback.reasons.incorrect'), value: NegativeFeedbackReasons.incorrect },
        { key: 'missingInfo', label: this.$t('knowledgeBase.feedback.reasons.missingInfo'), value: NegativeFeedbackReasons.missingInfo },
        { key: 'offTopic', label: this.$t('knowledgeBase.feedback.reasons.offTopic'), value: NegativeFeedbackReasons.offTopic }
      ]
    }
  },
  methods: {
    ...mapActions('knowledgeBase', ['closeSidecar', 'sendFeedback']),
    processMessageParts(message) {
      if (message.sender !== 'ai' || !message.text) {
        return [{ type: 'text', text: message.text || '' }]
      }

      if (!message.citations) {
        return [{ type: 'text', text: marked.parse(message.text) }]
      }

      const parts = []
      const citationMap = new Map(message.citations?.map(c => [c.id, c]))
      const regex = /\[(.*?)]/g

      let lastIndex = 0
      let match

      while ((match = regex.exec(message.text)) !== null) {
        const textPart = message.text.slice(lastIndex, match.index)
        if (textPart) {
          const parsedText = textPart.includes('\n')
            ? marked.parse(textPart)
            : marked.parseInline(textPart)
          parts.push({ type: 'text', text: parsedText })
        }

        const citationListStr = match[1]

        if (citationListStr) {
          const citationIds = citationListStr.split(',')
            .map(s => s.trim()).filter(id => id.startsWith('CITATION-'))
          const citations = citationIds.map(id => citationMap.get(id)).filter(Boolean)

          if (citations.length > 1) {
            const pageNumbers = citations.map(c => c.pageNumber)
            parts.push({ type: 'multiPageCitation', pageNumbers })
          } else if (citations.length === 1) {
            parts.push({ type: 'citation', pageNumber: citations[0].pageNumber, id: citations[0].id })
          }
        }

        lastIndex = match.index + match[0].length
      }

      const remainingText = message.text.slice(lastIndex)
      if (remainingText) {
        const parsedText = remainingText.includes('\n')
          ? marked.parse(remainingText)
          : marked.parseInline(remainingText)
        parts.push({ type: 'text', text: parsedText })
      }

      return parts
    },
    giveFeedback(feedbackType) {
      this.feedbackSubmitted = true
      if (feedbackType === 'up') {
        this.sendFeedback({ requestId: this.message.requestId, payload: { wasHelpful: true } })
      } else {
        this.sendFeedback({ requestId: this.message.requestId, payload: { wasHelpful: false } })
        this.selectingReason = true
      }
    },
    selectReason(reasonKey) {
      this.selectedReason = reasonKey
    },
    submitNegativeFeedback() {
      const reason = this.feedbackReasons.find(r => r.key === this.selectedReason)
      if (!reason) return

      this.sendFeedback({
        requestId: this.message.requestId,
        payload: { wasHelpful: false, reason: reason.value }
      })
      this.selectingReason = false
    },
    dismissAdditionalFeedback() {
      this.selectingReason = false
    },
    goToPage(pageNumber) {
      this.$store.dispatch('knowledgeBase/navigateToPageInDocument', pageNumber)
      if (window.innerWidth < 1024) this.closeSidecar()
    }
  }
}
</script>

<style scoped lang="scss">
.message-content {
  background-color: #f5f5f5;
  border-radius: 1.25rem 1.25rem 1.25rem 0.25rem;
  color: #363636;
  padding: 0.75rem 1rem;
  position: relative;
  overflow-wrap: break-word;
  word-break: break-word;
  word-wrap: break-word;

  .citation-link {
    color: #007bff;
    text-decoration: none;
  }
  .citation-link:hover {
    text-decoration: underline;
  }

  :deep(pre) {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  :deep(ol),
  :deep(ul) {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }

  :deep(ul > li) {
    list-style-type: circle;
  }
}

.feedback-reason-container {
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

.feedback-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.feedback-prompt {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.feedback-prompt-text {
  flex-grow: 1;
  margin-right: 1rem;
}

.delete {
  flex-shrink: 0;
}

.feedback-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.feedback-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: auto; /* Pushes actions to the bottom */
}

.feedback-buttons {
  display: flex;
  justify-content: flex-start;
}

.button.is-borderless {
  border-color: transparent;

  &:hover,
  &:focus {
    background-color: hsl(0, 0%, 96%);
  }
}

.thinking-animation::after {
  content: '.';
  animation: thinking 1s linear infinite;
}

@keyframes thinking {
  0%, 100% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
}
</style>
